'use client';

import { useOrganizationList, useUser } from '@clerk/nextjs';

import { planFromMetadata } from '../contracts/account';

// Check up to this many org memberships when deciding paid access — matches the
// server-side `ORG_MEMBERSHIP_LIMIT` in `resolvePaidAccess`, so the client gate and
// the API/MCP gate agree on who is paid. Keep in sync.
const ORG_MEMBERSHIP_PAGE_SIZE = 100;

export interface EffectivePlan {
  readonly plan: 'free' | 'paid';
  readonly isPaid: boolean;
  /** True once the user and org memberships have loaded. */
  readonly isLoaded: boolean;
}

/**
 * Resolve the user's effective plan the way the server does: paid if the personal
 * plan is paid OR *any* org membership is on a paid plan — the same policy
 * `resolvePaidAccess` enforces for the API / MCP. Shared by the account menu's gate
 * and the analytics identity stamp so the client `app.plan` is consistent and never
 * undercounts a paid-org user.
 */
export function useEffectivePlan(): EffectivePlan {
  const { user, isLoaded: userLoaded } = useUser();
  const { userMemberships, isLoaded: orgsLoaded } = useOrganizationList({
    userMemberships: { pageSize: ORG_MEMBERSHIP_PAGE_SIZE },
  });

  const isPaid =
    planFromMetadata(user?.publicMetadata) === 'paid' ||
    (userMemberships.data ?? []).some(
      (membership) =>
        planFromMetadata(membership.organization.publicMetadata) === 'paid',
    );

  return {
    plan: isPaid ? 'paid' : 'free',
    isPaid,
    isLoaded: Boolean(userLoaded) && Boolean(orgsLoaded),
  };
}
